import { z } from "zod";
import type { ProblemDetails } from "../shared";

// ==================== BASE SCHEMAS ====================

/**
 * Address schema for organization contacts
 */
export const AddressSchema = z.object({
	id: z.string().uuid(),
	createdBy: z.string().uuid().nullable(),
	createdAt: z.string().datetime().nullable(),
	createdAtOffset: z.string().nullable(),
	updatedBy: z.string().uuid().nullable(),
	updatedAt: z.string().datetime().nullable(),
	updatedAtOffset: z.string().nullable(),
	street1: z.string().nullable(),
	street2: z.string().nullable(),
	city: z.string().nullable(),
	state: z.string().nullable(),
	country: z.string().nullable(),
	postalCode: z
		.union([z.string().regex(/^\d{5}(-\d{4})?$/), z.literal("")])
		.nullable(),
});

/**
 * User status schema
 */
export const UserStatusSchema = z.object({
	code: z.number().int(),
	name: z.string().nullable(),
	description: z.string().nullable(),
});

/**
 * User schema (referenced by organization entities)
 */
export const UserSchema = z.object({
	id: z.string().uuid(),
	createdBy: z.string().uuid().nullable(),
	createdAt: z.string().datetime().nullable(),
	createdAtOffset: z.string().nullable(),
	updatedBy: z.string().uuid().nullable(),
	updatedAt: z.string().datetime().nullable(),
	updatedAtOffset: z.string().nullable(),
	displayName: z.string().nullable(),
	firstName: z.string().nullable(),
	middleName: z.string().nullable(),
	lastName: z.string().nullable(),
	email: z.string().nullable(),
	statusCode: z.number().int(),
	status: UserStatusSchema,
	lastLoggedInAt: z.string().datetime().nullable(),
	lastLoggedInAtOffset: z.string().nullable(),
	lastActiveOrganizationId: z.string().uuid().nullable(),
	// Added per swagger2.json diff - phone property for nested user in contacts
	phone: z.string().nullable(),
});

/**
 * Permission schema (simple version from system-wide roles API)
 */
export const PermissionSchema = z.object({
	id: z.string().uuid(),
	name: z.string().nullable(),
	displayName: z.string().nullable().optional(),
	description: z.string().nullable(),
	updatable: z.boolean().optional(),
});

/**
 * Global Role Permission schema (from /global-roles endpoint)
 * Simplified permission structure for global roles
 */
export const GlobalRolePermissionSchema = z.object({
	id: z.string().uuid(),
	name: z.string(),
	displayName: z.string(),
	description: z.string(),
});

/**
 * Global Role schema (from /global-roles endpoint)
 * Represents both Platform and Organization scoped roles
 */
export const GlobalRoleSchema = z.object({
	id: z.string(),
	name: z.string(),
	description: z.string().nullable(),
	scope: z.enum(["Platform", "Organization"]),
	adminRole: z.boolean(),
	permissions: z.array(GlobalRolePermissionSchema),
	activeUserCount: z.number().nullish(),
});

/**
 * System-wide Organization Role schema (from /organization-roles endpoint)
 * This is the simplified version returned by the system-wide roles API
 */
export const SystemOrganizationRoleSchema = z.object({
	id: z.string().uuid(),
	organizationId: z.string(),
	name: z.string().nullable(),
	description: z.string().nullable().optional(),
	adminRole: z.boolean(),
	custom: z.boolean().optional(),
	updatable: z.boolean(),
	permissionIds: z.array(z.string().uuid()),
	validForSubOrganizationUser: z.boolean().nullable(),
	validForSubContractorUser: z.boolean().nullable(),
	isFullDataAccessRole: z.boolean().nullable().optional(),
	permissions: z.array(PermissionSchema),
});

/**
 * Organization role permission schema (legacy format)
 */
export const OrganizationRolePermissionSchema = z.object({
	id: z.string().uuid(),
	createdBy: z.string().uuid().nullable(),
	createdAt: z.string().datetime().nullable(),
	createdAtOffset: z.string().nullable(),
	updatedBy: z.string().uuid().nullable(),
	updatedAt: z.string().datetime().nullable(),
	updatedAtOffset: z.string().nullable(),
	organizationRoleId: z.string().uuid(),
	organizationId: z.string().uuid(),
	permissionId: z.string().uuid(),
	permission: PermissionSchema,
});

/**
 * Organization role list item schema
 * Matches actual response from GET /organizations/{id}/roles
 */
export const OrganizationRoleListItemSchema = z.object({
	organizationId: z.string().optional(),
	updatable: z.boolean().optional(),
	validForSubOrganizationUser: z.boolean().optional(),
	validForSubContractorUser: z.boolean().optional(),
	isFullDataAccessRole: z.boolean().optional(),
	id: z.string().uuid(),
	name: z.string(),
	description: z.string().nullable().optional(),
	adminRole: z.boolean().optional(),
	custom: z.boolean().optional(),
	activeUserCount: z.number().nullish(),
	permissionIds: z.array(z.string().uuid()).optional(),
	permissions: z.array(PermissionSchema).optional(),
});

/**
 * Organization role schema for user display endpoint
 * This matches the structure returned by GET /organizations/{id}/users/{userId}
 * Note: organizationId, updatable, validForSubOrganizationUser, validForSubContractorUser
 * were removed from API response (see diff-report.md)
 */
export const OrganizationRoleSchema = z.object({
	id: z.string().uuid(),
	name: z.string().nullable(),
	description: z.string().nullable(),
	adminRole: z.boolean(),
	permissionIds: z.array(z.string().uuid()).nullable(),
	permissions: z.array(PermissionSchema).nullable(),
});

/**
 * Organization status enum
 */
export enum OrganizationStatusCode {
	Created = 0,
	Active = 1,
	Suspended = 2,
	Archived = 3,
	Retired = 4,
}

/**
 * Color mapping for organization status badges
 */
export const OrganizationStatusColorMap: Record<
	number,
	"success" | "gray" | "warning" | "error" | "blue-light"
> = {
	0: "blue-light", // Created
	1: "success", // Active
	2: "warning", // Suspended
	3: "gray", // Archived
	4: "error", // Retired
};

/**
 * Organization status name enum
 */
export const OrganizationStatusName = z.enum([
	"Created",
	"Active",
	"Suspended",
	"Archived",
	"Retired",
]);

/**
 * Simplified address schema for list endpoints (without base entity fields)
 * Used by sub-contractor and sub-organization list responses
 * Note: street2 and country can be null in API responses
 */
export const SimpleAddressSchema = z.object({
	street1: z.string().nullable(),
	street2: z.string().nullable(),
	city: z.string().nullable(),
	state: z.string().nullable(),
	country: z.string().nullable(),
	postalCode: z.string().nullable(),
});

/**
 * Sub-Organization list item schema (lightweight for list endpoint)
 * Matches actual API response from GET /organizations/{id}/sub-organizations
 */
export const SubOrganizationStatusSchema = z.object({
	code: z.number().int(),
	name: z.string(),
	description: z.string().nullable().optional(),
});

export const SubOrganizationListItemSchema = z.object({
	id: z.string().uuid(),
	name: z.string(),
	phone: z.string(),
	statusCode: z.number().int().optional(),
	status: SubOrganizationStatusSchema.nullable().optional(),
	totalAssignedUserCount: z.number().int(),
	usersWithFullDataAccessCount: z.number().int(),
	usersWithLimitedDataAccessCount: z.number().int(),
	address: SimpleAddressSchema,
	created: z.string().nullable().optional(),
	lastUpdated: z.string().nullable().optional(),
});

/**
 * Sub-Contractor list item schema (lightweight for list endpoint)
 * Matches actual API response from GET /organizations/{id}/sub-contractors/{start}/{count}
 *
 * Note: List endpoint returns simplified objects without audit fields.
 * Fields like phone, totalAssignedUserCount, address may be null.
 */
export const SubContractorListItemSchema = z.object({
	id: z.string().uuid(),
	name: z.string(),
	phone: z.string().nullable(),
	totalAssignedUserCount: z.number().int().nullable(),
	address: SimpleAddressSchema.nullable(),
	statusCode: z.number().int().optional(),
	created: z.string().nullable().optional(),
	lastUpdated: z.string().nullable().optional(),
});

/**
 * Organization status schema
 */
export const OrganizationStatusSchema = z.object({
	code: z.nativeEnum(OrganizationStatusCode),
	name: OrganizationStatusName.nullable(),
	description: z.string().nullable(),
});

/**
 * OAuth provider schema
 */
export const OAuthProviderSchema = z.object({
	id: z.string().uuid().optional(),
	createdBy: z.string().uuid().nullable().optional(),
	createdAt: z.string().datetime().nullable().optional(),
	createdAtOffset: z.string().nullable().optional(),
	updatedBy: z.string().uuid().nullable().optional(),
	updatedAt: z.string().datetime().nullable().optional(),
	updatedAtOffset: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	clientId: z.string().nullable().optional(),
	clientSecret: z.string().nullable().optional(),
	authorizeEndpointUrl: z.string().nullable().optional(),
	tokenEndpointUrl: z.string().nullable().optional(),
	userIdClaimOverride: z.string().nullable().optional(), // Can be undefined in API response
	emailClaimOverride: z.string().nullable().optional(),
	userNameClaimOverride: z.string().nullable().optional(),
	firstNameClaim: z.string().nullable().optional(),
	lastNameClaim: z.string().nullable().optional(),
	phoneClaim: z.string().nullable().optional(),
	logOutEndpointUrl: z.string().nullable().optional(), // Added from API response
});

/**
 * Organization schema
 */
export const OrganizationSchema = z.object({
	id: z.string().uuid(),
	organizationId: z.string().uuid().optional(), // API returns this as duplicate of id
	createdBy: z.string().uuid().nullable(),
	createdAt: z.string().datetime().nullable(),
	createdAtOffset: z.string().nullable(),
	updatedBy: z.string().uuid().nullable(),
	updatedAt: z.string().datetime().nullable(),
	updatedAtOffset: z.string().nullable(),
	name: z.string().nullable(),
	statusCode: z.number().int(),
	oAuthProviderId: z.string().uuid().nullable(),
	oAuthProvider: OAuthProviderSchema.nullable(),
	status: OrganizationStatusSchema,
	mfaEnabled: z.boolean().nullable(),
	mfaRequired: z.boolean().nullable().optional(),
	totalUserCount: z.number().int().optional(), // New field from swagger2.json
});

/**
 * Organization contact type schema
 */
export const OrganizationContactTypeSchema = z.object({
	code: z.number().int(),
	name: z.string().nullable(),
	description: z.string().nullable(),
});

/**
 * Organization contact schema
 * API returns id + contactType (string) instead of contactTypeCode (int) +
 * organizationContactType (object). Legacy numeric fields kept as optional for
 * any older API versions still in circulation.
 */
export const OrganizationContactSchema = z.object({
	id: z.string().uuid(),
	createdBy: z.string().uuid().nullable().optional(),
	createdAt: z.string().datetime().nullable().optional(),
	createdAtOffset: z.string().nullable().optional(),
	updatedBy: z.string().uuid().nullable().optional(),
	updatedAt: z.string().datetime().nullable().optional(),
	updatedAtOffset: z.string().nullable().optional(),
	organizationId: z.string().uuid(),
	contactType: z.string(),
	contactTypeCode: z.number().int().optional(),
	organizationContactType: OrganizationContactTypeSchema.optional(),
	userId: z.string().uuid().nullable(),
	user: UserSchema.nullable(),
	title: z.string().nullable(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	email: z.string().nullable(),
	phone: z.string().nullable(),
	addressId: z.string().uuid().nullable(),
	address: SimpleAddressSchema.nullable(),
});

/**
 * Organization user schema
 */
export const OrganizationUserSchema = z.object({
	id: z.string().uuid(),
	createdBy: z.string().uuid().nullable(),
	createdAt: z.string().datetime().nullable(),
	createdAtOffset: z.string().nullable(),
	updatedBy: z.string().uuid().nullable(),
	updatedAt: z.string().datetime().nullable(),
	updatedAtOffset: z.string().nullable(),
	userId: z.string().uuid(),
	user: UserSchema.nullable(),
	organizationId: z.string().uuid(),
	oAuthProviderUserId: z.string().nullable(),
	statusCode: z.number().int(),
	status: UserStatusSchema,
	title: z.string().nullable(),
	phone: z.string().nullable(),
	displayNameOverride: z.string().nullable(),
	belongsTo: z.string().nullable(),
});

/**
 * Organization user display schema (for list views and detail views)
 * Returned by GET /organizations/{organizationId}/users and GET /organizations/{organizationId}/users/{organizationUserId}
 * Note: API is inconsistent - some fields return "" (empty string) and others return null
 */
export const OrganizationUserDisplaySchema = z.object({
	id: z.string(),
	userId: z.string(),
	hasAvatar: z.boolean().optional(),
	email: z.string(),
	firstName: z.string(),
	middleName: z.string().nullable(),
	lastName: z.string(),
	displayName: z.string().nullable(),
	title: z.string().nullable(),
	phone: z.string().nullable(),
	status: UserStatusSchema,
	organizationRoles: z.array(OrganizationRoleSchema),
	created: z.string(),
	activated: z.string().nullable(),
	lastUpdated: z.string(),
	lastLoggedIn: z.string().nullable(),
	lastStatusUpdated: z.string().nullable(),
	// Related entities - can be null
	// NOTE: Using passthrough() because nested objects have different shapes than their standalone schemas
	subContractor: z
		.object({
			id: z.string(),
			name: z.string(),
		})
		.passthrough()
		.nullable(),
	subOrganization: z
		.object({
			id: z.string(),
			name: z.string(),
		})
		.passthrough()
		.nullable(),
	// NOTE: API returns full organization object with many fields, using passthrough for flexibility
	organization: z
		.object({
			id: z.string(),
			name: z.string().nullable(),
		})
		.passthrough()
		.nullable(),
	// NEW: Added per diff-report.md - indicates which entity the user belongs to
	belongsTo: z.string().nullable(),
	// NEW: Added per diff-report.md - comma-separated list of role names
	roles: z.string().nullable(),
	createdByUser: z.string().nullable().optional(),
	updatedByUser: z.string().nullable().optional(),
});

/**
 * Sub-organization schema
 */
export const SubOrganizationSchema = z.object({
	id: z.string().uuid(),
	createdBy: z.string().uuid().nullable(),
	createdAt: z.string().datetime().nullable(),
	createdAtOffset: z.string().nullable(),
	updatedBy: z.string().uuid().nullable(),
	updatedAt: z.string().datetime().nullable(),
	updatedAtOffset: z.string().nullable(),
	name: z.string().min(1).max(50),
	organizationId: z.string().uuid(),
	addressId: z.string().uuid().nullable(),
	address: AddressSchema.nullable(),
});

/**
 * Sub-organization display schema (for user detail/list views)
 * Updated to match swagger2.json API response
 */
export const SubOrganizationDisplaySchema = z.object({
	id: z.string().nullable(),
	name: z.string().nullable(),
	phone: z.string().nullable().optional(),
	statusCode: z.number().int().optional(),
	status: SubOrganizationStatusSchema.nullable().optional(),
	totalAssignedUserCount: z.number().int().optional(),
	usersWithFullDataAccessCount: z.number().int().optional(),
	usersWithLimitedDataAccessCount: z.number().int().optional(),
	address: SimpleAddressSchema.nullable().optional(),
	created: z.string().nullable().optional(),
	lastUpdated: z.string().nullable().optional(),
	createdByUser: z.string().nullable().optional(),
	updatedByUser: z.string().nullable().optional(),
});

/**
 * Sub-contractor schema
 */
export const SubContractorSchema = z.object({
	id: z.string().uuid(),
	createdBy: z.string().uuid().nullable(),
	createdAt: z.string().datetime().nullable(),
	createdAtOffset: z.string().nullable(),
	updatedBy: z.string().uuid().nullable(),
	updatedAt: z.string().datetime().nullable(),
	updatedAtOffset: z.string().nullable(),
	name: z.string().min(1).max(50),
	organizationId: z.string().uuid(),
	addressId: z.string().uuid().nullable(),
	address: AddressSchema.nullable(),
});

/**
 * Sub-contractor ↔ sub-organization access row from
 * GET /organizations/{id}/sub-contractors/{subContractorId}
 */
export const SubContractorAccessSchema = z.object({
	subOrganizationId: z.string().nullable().optional(),
	subContractorId: z.string().nullable().optional(),
	deleted: z.boolean().optional(),
});

/**
 * Sub-contractor display schema (for user detail/list views)
 * Updated to match actual API response from:
 * GET /organizations/{id}/sub-contractors/{subContractorId}
 *
 * Note: Using lenient validation for addressId (API may return non-RFC 4122 UUIDs)
 * and datetime fields (handles ISO 8601 with microsecond precision)
 */
export const SubContractorDisplaySchema = z.object({
	// Core identification
	id: z.string().uuid(),
	name: z.string(),
	statusCode: z.number().int().optional(),
	// Address fields - addressId uses lenient string validation (API returns non-standard UUIDs)
	address: SimpleAddressSchema.nullable(),
	// Contact & assignment info
	phone: z.string().nullable(),
	totalAssignedUserCount: z.number().int().nullable(),
	subContractorAccess: z.array(SubContractorAccessSchema).optional(),
	// Audit fields
	created: z.string().nullable(),
	lastUpdated: z.string().nullable(),
	createdByUser: z.string().nullable().optional(),
	updatedByUser: z.string().nullable().optional(),
});

/**
 * Data access info schema
 * Defines data access permissions for organization users
 * Updated in swagger2.json to include organizationUserId
 */
export const DataAccessInfoSchema = z.object({
	organizationUserId: z.string().uuid().optional(),
	subOrganizationId: z.string().nullable().optional(),
	fullAccess: z.boolean(),
});

/**
 * Data access display schema
 * Returned from GET /organizations/{id}/users/{userId}/data-access/{start}/{count}
 */
export const DataAccessDisplaySchema = z.object({
	organizationUserId: z.string().nullable().optional(),
	subOrganizationId: z.string().nullable().optional(),
	fullAccess: z.boolean(),
});

/**
 * Simplified organization schema for audit logs
 * Updated in swagger2.json - audit logs now return minimal organization info
 */
export const AuditLogOrganizationSchema = z.object({
	id: z.string().uuid().nullable(),
	name: z.string().nullable().optional(),
});

/**
 * Simplified user schema for audit logs
 * Updated in swagger2.json - audit logs now return minimal user info
 */
export const AuditLogUserSchema = z.object({
	id: z.string().uuid().nullable(),
	hasAvatar: z.boolean().optional(),
	displayName: z.string().nullable().optional(),
	firstName: z.string().nullable().optional(),
	middleName: z.string().nullable().optional(),
	lastName: z.string().nullable().optional(),
	email: z.string().nullable().optional(),
});

/**
 * Audit log entry schema
 * Updated in swagger2.json with simplified organization and user objects
 */
export const AuditLogSchema = z.object({
	id: z.string().uuid(),
	organizationId: z.string().uuid().nullable(),
	entityId: z.string().uuid().nullable(),
	userId: z.string().uuid().nullable(),
	sessionId: z.string().uuid().nullable(),
	organization: AuditLogOrganizationSchema.nullable(),
	user: AuditLogUserSchema.nullable(),
	action: z.string().nullable(),
	source: z.string().nullable(),
	userAgent: z.string().nullable(),
	ipAddress: z.string().nullable(),
	entityType: z.string().nullable(),
	fieldName: z.string().nullable(),
	oldValue: z.string().nullable(),
	newValue: z.string().nullable(),
	createdAt: z.string().datetime().nullable(),
	createdAtOffset: z.string().nullable(),
});

/**
 * Service result schema for API responses
 */
export const ServiceResultSchema = z.object({
	success: z.boolean(),
	affectedId: z.string().nullable().optional(),
	errors: z.array(z.string()).optional(),
});

export type { ProblemDetails } from "../shared";
/**
 * Problem details schema for error responses
 */
export { ProblemDetailsSchema } from "../shared";

export const OrganizationStatusItemSchema = z.object({
	code: z.number().int(),
	name: z.string().nullable(),
	description: z.string().nullable(),
});

/**
 * Extended Organization Display schema
 * Used in audit logs, user display, and user session responses
 * Contains full organization details including audit fields
 * Based on swagger2.json OrganizationDisplay schema
 */
export const ExtendedOrganizationDisplaySchema = z.object({
	id: z.string().uuid(),
	name: z.string().nullable(),
	statusCode: z.number().int(),
	oAuthProviderId: z.string().uuid().nullable(),
	oAuthProvider: OAuthProviderSchema.nullable(),
	status: OrganizationStatusSchema,
	mfaEnabled: z.boolean().nullable(),
	totalUserCount: z.number().int(),
	organizationId: z.string().uuid().optional(), // readOnly in API
	createdBy: z.string().uuid().nullable(),
	createdAt: z.string().nullable(),
	createdAtOffset: z.string().nullable(),
	updatedBy: z.string().uuid().nullable(),
	updatedAt: z.string().nullable(),
	updatedAtOffset: z.string().nullable(),
	// Additional fields for user invite/session responses
	firstPartyAuthOrganizationId: z.string().uuid().nullable().optional(),
	mfaRequired: z.boolean().nullable().optional(),
	/** Display name supplied directly by the backend — no secondary lookup needed */
	createdByUser: z.string().nullable().optional(),
	updatedByUser: z.string().nullable().optional(),
});

/**
 * Check email usage schema
 * Response from GET /organizations/{organizationId}/users/check-email
 * Used to verify if an email is already in use globally or within the organization
 */
export const CheckEmailUsageSchema = z.object({
	email: z.string().nullable(),
	inUseGlobally: z.boolean(),
	inUseInThisOrganization: z.boolean(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
});

/**
 * Create sub-contractor info schema
 * Request body for POST /organizations/{organizationId}/sub-contractors
 * Based on swagger2.json CreateSubContractorInfo schema
 */
export const CreateSubContractorInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	name: z.string().nullable(),
	phone: z.string().nullable(),
	address: AddressSchema.nullable().optional(),
});

/**
 * Update sub-contractor info schema
 * Request body for PUT /organizations/{organizationId}/sub-contractors/{subContractorId}
 */
export const UpdateSubContractorInfoSchema = z.object({
	name: z.string().nullable(),
	phone: z.string().nullable(),
	address: z
		.object({
			street1: z.string().nullable().optional(),
			street2: z.string().nullable().optional(),
			city: z.string().nullable().optional(),
			state: z.string().nullable().optional(),
			country: z.string().nullable().optional(),
			postalCode: z.string().nullable().optional(),
		})
		.nullable()
		.optional(),
});

// ==================== INFERRED TYPES ====================

export type Address = z.infer<typeof AddressSchema>;
export type UserStatus = z.infer<typeof UserStatusSchema>;
export type User = z.infer<typeof UserSchema>;
export type Permission = z.infer<typeof PermissionSchema>;
export type GlobalRolePermission = z.infer<typeof GlobalRolePermissionSchema>;
export type GlobalRole = z.infer<typeof GlobalRoleSchema>;
export type SystemOrganizationRole = z.infer<
	typeof SystemOrganizationRoleSchema
>;
export type OrganizationRolePermission = z.infer<
	typeof OrganizationRolePermissionSchema
>;
export type OrganizationRole = z.infer<typeof OrganizationRoleSchema>;
export type OrganizationRoleListItem = z.infer<
	typeof OrganizationRoleListItemSchema
>;
export type SubOrganizationListItem = z.infer<
	typeof SubOrganizationListItemSchema
>;
export type SimpleAddress = z.infer<typeof SimpleAddressSchema>;
export type SubContractorListItem = z.infer<typeof SubContractorListItemSchema>;
export type OrganizationStatus = z.infer<typeof OrganizationStatusSchema>;
export type OAuthProvider = z.infer<typeof OAuthProviderSchema>;
export type Organization = z.infer<typeof OrganizationSchema>;
export type OrganizationContactType = z.infer<
	typeof OrganizationContactTypeSchema
>;
export type OrganizationContact = z.infer<typeof OrganizationContactSchema>;
export type OrganizationUser = z.infer<typeof OrganizationUserSchema>;
export type OrganizationUserDisplay = z.infer<
	typeof OrganizationUserDisplaySchema
>;
export type SubOrganization = z.infer<typeof SubOrganizationSchema>;
export type SubOrganizationDisplay = z.infer<
	typeof SubOrganizationDisplaySchema
>;
export type SubContractor = z.infer<typeof SubContractorSchema>;
export type SubContractorDisplay = z.infer<typeof SubContractorDisplaySchema>;
export type DataAccessInfo = z.infer<typeof DataAccessInfoSchema>;
export type DataAccessDisplay = z.infer<typeof DataAccessDisplaySchema>;
export type AuditLogOrganization = z.infer<typeof AuditLogOrganizationSchema>;
export type AuditLogUser = z.infer<typeof AuditLogUserSchema>;
export type AuditLog = z.infer<typeof AuditLogSchema>;
export type ServiceResult = z.infer<typeof ServiceResultSchema>;
export type OrganizationStatusItem = z.infer<
	typeof OrganizationStatusItemSchema
>;
export type GetOrganizationStatusesResponse = OrganizationStatusItem[];
export type ExtendedOrganizationDisplay = z.infer<
	typeof ExtendedOrganizationDisplaySchema
>;
export type CheckEmailUsage = z.infer<typeof CheckEmailUsageSchema>;
export type CreateSubContractorInfo = z.infer<
	typeof CreateSubContractorInfoSchema
>;
export type UpdateSubContractorInfo = z.infer<
	typeof UpdateSubContractorInfoSchema
>;

// ==================== CREATE/UPDATE SCHEMAS ====================

/**
 * Enhanced create organization info schema
 * Supports comprehensive organization creation with OAuth and contact details
 */
export const CreateOrganizationInfoSchema = z.object({
	name: z.string().min(1, "Organization name is required"),
	// oAuthProviderId: z.string().uuid().optional(),
	// initialStatus: z.string().optional(),
	// contactInfo: z
	// 	.object({
	// 		name: z.string().optional(),
	// 		email: z.string().email().optional(),
	// 		phone: z.string().optional(),
	// 		address: z.string().optional(),
	// 	})
	// 	.optional(),
	// // Additional fields for enhanced organization creation
	// companyWebsite: z.string().url().optional(),
	// tenantId: z.string().optional(),
	// clientId: z.string().optional(),
	// clientSecret: z.string().optional(),
});

/**
 * Update organization info schema
 * Supports updating both name and status in a single payload
 * Updated: Added mfaRequired per diff-report.md
 */
export const UpdateOrganizationInfoSchema = z.object({
	name: z.string().nullable().optional(),
	status: z.string().nullable().optional(),
	mfaRequired: z.boolean().optional(),
});

/**
 * Update organization status schema
 */
export const UpdateOrganizationStatusSchema = z.object({
	status: z.string().nullable(),
});

/**
 * Organization role info schema (for create/update)
 */
export const OrganizationRoleInfoSchema = z.object({
	name: z.string().nullable(),
	permissionIds: z.array(z.string()).nullable(),
	description: z.string().nullable().optional(),
	adminRole: z.boolean().optional(),
	validForSubOrganizationUser: z.boolean().nullable().optional(),
	validForSubContractorUser: z.boolean().nullable().optional(),
	isFullDataAccessRole: z.boolean().nullable().optional(),
});

/**
 * Update address schema
 * All fields are optional for partial updates; no postal code format restriction.
 */
export const UpdateAddressSchema = z.object({
	id: z.string().uuid().optional(),
	createdBy: z.string().uuid().nullable().optional(),
	createdAt: z.string().datetime().nullable().optional(),
	createdAtOffset: z.string().nullable().optional(),
	updatedBy: z.string().uuid().nullable().optional(),
	updatedAt: z.string().datetime().nullable().optional(),
	updatedAtOffset: z.string().nullable().optional(),
	street1: z.string().nullable().optional(),
	street2: z.string().nullable().optional(),
	city: z.string().nullable().optional(),
	state: z.string().nullable().optional(),
	country: z.string().nullable().optional(),
	postalCode: z.string().nullable().optional(),
});

/**
 * Update organization contact info schema
 */
export const UpdateOrganizationContactInfoSchema = z.object({
	userId: z.string().nullable(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	email: z.string().nullable(),
	title: z.string().nullable().optional(),
	phone: z.string().nullable(),
	address: UpdateAddressSchema.nullable(),
});

/**
 * Organization user info schema (for CREATE)
 * Used for POST requests to create organization users
 * Based on swagger2.json OrganizationUserInfo schema (lines 6121-6168)
 *
 * API REQUIRES these fields to be present (even if null):
 * - firstName, lastName, email, phone (basic info - MUST be included)
 * - displayName, title (display info - MUST be included even if null)
 * - roleId (required - which role to assign)
 * - subOrganizationId, subContractorId (assignment - MUST be included even if null)
 * - dataAccess (optional - array of sub-org access rules)
 */
export const CreateOrganizationUserInfoSchema = z.object({
	// Basic required fields - MUST be present
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	email: z.string().nullable(),

	// Phone must be present (string or null) - API requires this field
	phone: z.string().nullable(),

	// Display fields - MUST be present even if null
	displayName: z.string().nullable(),
	title: z.string().nullable(),

	// Required: Role assignment
	roleId: z.string().nullable(),

	// Assignment fields - MUST be present even if null
	subOrganizationId: z.string().nullable(),
	subContractorId: z.string().nullable(),

	// Optional: Data access rules (can be omitted or null)
	dataAccess: z
		.array(
			z.object({
				subOrganizationId: z.string(),
				fullAccess: z.boolean(),
			}),
		)
		.nullable()
		.optional(),

	// Optional: Full read access (API OrganizationUserInfo)
	fullReadAccess: z.boolean().nullable().optional(),
});

/**
 * Update organization user info schema (for UPDATE)
 * Used for PUT requests to update organization users
 * Based on swagger2.json UpdateOrganizationUserInfo schema
 * NOTE: Does NOT include firstName, lastName, or email - those are user-level properties
 * Updated: Added dataAccess property per diff-report.md changes
 */
export const UpdateOrganizationUserInfoSchema = z.object({
	displayName: z.string().nullable().optional(),
	title: z.string().nullable().optional(),
	phone: z.string().nullable().optional(),
	roleId: z.string().nullable().optional(),
	subOrganizationId: z.string().nullable().optional(),
	subContractorId: z.string().nullable().optional(),
	status: z.string().nullable().optional(),
	// NEW: Data access array for sub-organization permissions (swagger2.json)
	dataAccess: z
		.array(
			z.object({
				subOrganizationId: z.string(),
				fullAccess: z.boolean(),
			}),
		)
		.nullable()
		.optional(),
});

/**
 * Organization user info schema from API responses
 * Returned by GET /users/{userId} endpoint in organizationUserInfo array
 * Contains comprehensive user information including organization membership details
 */
export const OrganizationUserInfoSchema = z
	.object({
		id: z
			.string()
			.regex(
				/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
			), // UUID format (not strictly v4) - backend returns UUIDs that don't conform to strict v4
		userId: z.string().uuid().optional(), // User ID reference
		hasAvatar: z.boolean().optional(),
		displayName: z.string().nullable(), // Can be null/empty in actual API responses
		title: z.string().nullable(),
		phone: z.string().nullable(),
		// Datetime fields for organization user
		created: z.string().nullable(),
		activated: z.string().nullable(),
		lastUpdated: z.string().nullable(),
		lastLoggedIn: z.string().nullable(),
		lastStatusUpdated: z.string().nullable().optional(), // Last status update timestamp
		status: UserStatusSchema, // Required
		organizationRoles: z.array(OrganizationRoleSchema).default([]), // Required, default to empty array
		roles: z.string().nullable().optional(), // Roles as string (legacy field)
		subContractor: z
			.object({
				id: z.string().uuid().nullable().optional(),
				name: z.string().nullable(),
				address: AddressSchema.nullable().optional(),
			})
			.nullable(),
		subOrganization: z
			.object({
				id: z.string().uuid().nullable().optional(),
				name: z.string().nullable(),
				phone: z.string().nullable().optional(),
				totalAssignedUserCount: z.number().int().nullable().optional(),
				usersWithFullDataAccessCount: z.number().int().nullable().optional(),
				usersWithLimitedDataAccessCount: z.number().int().nullable().optional(),
				address: SimpleAddressSchema.nullable().optional(),
				created: z.string().nullable().optional(),
				lastUpdated: z.string().nullable().optional(),
			})
			.passthrough()
			.nullable(),
		organization: OrganizationSchema.nullable(), // Full organization object from API
		canViewOrganizationDetails: z.boolean().optional(),
		belongsTo: z.string().nullable(),
	})
	.passthrough(); // Allow extra fields that may be returned by API

/**
 * Update organization user status schema
 */
export const UpdateOrganizationUserStatusSchema = z.object({
	status: z.string().nullable(),
});

/**
 * Create sub-organization info schema
 * Used for POST /organizations/{organizationId}/sub-organizations
 * Based on swagger2.json CreateSubOrganizationInfo schema
 */
export const CreateSubOrganizationInfoSchema = z.object({
	// organizationId: z.string().nullable().optional(),
	name: z.string().nullable(),
	phone: z.string().nullable(),
	address: AddressSchema.nullable().optional(),
});

/**
 * Update sub-organization contact info schema
 * Used for PUT /organizations/{organizationId}/sub-organizations/{subOrganizationId}
 * Based on swagger2.json UpdateSubOrganizationContactInfo schema
 */
export const UpdateSubOrganizationContactInfoSchema = z.object({
	name: z.string().nullable(),
	phone: z.string().nullable(),
	address: UpdateAddressSchema.nullable().optional(),
});

// ==================== CREATE/UPDATE TYPES ====================

export type CreateOrganizationInfo = z.infer<
	typeof CreateOrganizationInfoSchema
>;
export type UpdateOrganizationInfo = z.infer<
	typeof UpdateOrganizationInfoSchema
>;
export type UpdateOrganizationStatus = z.infer<
	typeof UpdateOrganizationStatusSchema
>;
export type OrganizationRoleInfo = z.infer<typeof OrganizationRoleInfoSchema>;
export type UpdateOrganizationContactInfo = z.infer<
	typeof UpdateOrganizationContactInfoSchema
>;
export type CreateOrganizationUserInfo = z.infer<
	typeof CreateOrganizationUserInfoSchema
>;
export type UpdateOrganizationUserInfo = z.infer<
	typeof UpdateOrganizationUserInfoSchema
>;
export type OrganizationUserInfo = z.infer<typeof OrganizationUserInfoSchema>;
export type UpdateOrganizationUserStatus = z.infer<
	typeof UpdateOrganizationUserStatusSchema
>;
export type CreateSubOrganizationInfo = z.infer<
	typeof CreateSubOrganizationInfoSchema
>;
export type UpdateSubOrganizationContactInfo = z.infer<
	typeof UpdateSubOrganizationContactInfoSchema
>;

// ==================== FILTER AND PAGINATION TYPES ====================

/**
 * Organization filters for list queries
 */
export type OrganizationParams = {
	SearchTerm?: string;
	Status?: number;
	Statuses?: string;
	OrganizationIds?: string;
	UsesThirdPartyAuthentication?: boolean;
	CreatedBefore?: string;
	CreatedAfter?: string;
	UpdatedBefore?: string;
	UpdatedAfter?: string;
	"OrderBy.Fields"?: string[];
	"OrderBy.Descending"?: boolean;
	columns?: string;
	SubContractorId?: string;
};

/**
 * Organization users filters
 *
 * NEW parameters added per diff-report.md:
 * - ActivatedBefore: Filter users activated before date (ISO 8601)
 * - ActivatedAfter: Filter users activated after date (ISO 8601)
 */
export type OrganizationUsersParams = {
	OrganizationUserId?: string;
	UserId?: string;
	SearchTerm?: string;
	Email?: string;
	Status?: number;
	Statuses?: string;
	HasPlatformRoles?: boolean;
	HasOrganizationUsers?: boolean;
	CreatedBefore?: string;
	CreatedAfter?: string;
	UpdatedBefore?: string;
	UpdatedAfter?: string;
	LastLoggedInBefore?: string;
	LastLoggedInAfter?: string;
	// NEW: Added per diff-report.md - Filter by activation date
	ActivatedBefore?: string;
	ActivatedAfter?: string;
	OrganizationId?: string;
	SubOrganizationIds?: string[] | string;
	SubContractorIds?: string[] | string;
	OrganizationRoleIds?: string[];
	IsAdmin?: boolean;
	PlatformRoleIds?: number[];
	OrderBy?: {
		Fields: string[];
		Descending?: boolean;
	};
};

/**
 * Filter parameters for data access queries
 */
export type DataAccessFilters = {
	includeSoftDeleted?: boolean;
	organizationId?: string;
	organizationUserId?: string;
	subOrganizationId?: string;
	hasFullDataAccess?: boolean;
	orderBy?: {
		fields: string[];
		descending?: boolean;
	};
};

/**
 * Pagination parameters
 */
export type PaginationParams = {
	start: number;
	count: number;
};

/**
 * API response wrapper
 */
export type ApiResponse<T> = {
	data: T;
	message?: string;
	status: "success" | "error";
};

/**
 * Paginated response wrapper
 */
export type PaginatedResponse<T> = {
	data: T[];
	total: number;
	page: number;
	limit: number;
	hasMore: boolean;
};

// ==================== ERROR HANDLING ====================

/**
 * Custom error class for Organization API operations
 */
export class OrganizationApiError extends Error {
	constructor(
		message: string,
		public error: unknown,
		public code?: string,
		public status?: number,
		public details?: unknown,
	) {
		super(message);
		this.name = "OrganizationApiError";
	}

	/**
	 * Create error from API response
	 */
	static fromResponse(
		response: Response,
		data?: unknown,
	): OrganizationApiError {
		const message = `API Error: ${response.status} ${response.statusText}`;
		return new OrganizationApiError(
			message,
			undefined,
			response.statusText,
			response.status,
			data,
		);
	}

	/**
	 * Create error from problem details
	 */
	static fromProblemDetails(
		problemDetails: ProblemDetails,
	): OrganizationApiError {
		const message =
			problemDetails.title || problemDetails.detail || "Unknown API error";
		return new OrganizationApiError(
			message,
			undefined,
			problemDetails.type || undefined,
			problemDetails.status || undefined,
			problemDetails,
		);
	}

	/**
	 * Create error from service result
	 */
	static fromServiceResult(serviceResult: ServiceResult): OrganizationApiError {
		const message =
			serviceResult.errors?.join(", ") || "Service operation failed";
		return new OrganizationApiError(
			message,
			undefined,
			"SERVICE_ERROR",
			undefined,
			serviceResult,
		);
	}
}

// ==================== VALIDATION HELPERS ====================

/**
 * Validate organization ID format
 */
export function isValidOrganizationId(id: string): boolean {
	return z.string().uuid().safeParse(id).success;
}

/**
 * Validate user ID format
 */
export function isValidUserId(id: string): boolean {
	return z.string().uuid().safeParse(id).success;
}

/**
 * Validate role ID format
 */
export function isValidRoleId(id: string): boolean {
	return z.string().uuid().safeParse(id).success;
}

/**
 * Validate contact ID format
 */
export function isValidContactId(id: string): boolean {
	return z.string().uuid().safeParse(id).success;
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
	return z.string().email().safeParse(email).success;
}

/**
 * Validate postal code format (US format)
 */
export function isValidPostalCode(postalCode: string): boolean {
	return /^\d{5}(-\d{4})?$/.test(postalCode);
}

// ==================== ARRAY SCHEMAS ====================

// Array schemas for list responses
export const OrganizationsArraySchema = z.array(OrganizationSchema);
export const GlobalRolesArraySchema = z.array(GlobalRoleSchema);
export const OrganizationRolesArraySchema = z.array(OrganizationRoleSchema);
export const OrganizationRoleListItemsArraySchema = z.array(
	OrganizationRoleListItemSchema,
);
export const OrganizationContactsArraySchema = z.array(
	OrganizationContactSchema,
);
export const OrganizationUsersArraySchema = z.array(OrganizationUserSchema);
export const OrganizationUserDisplayArraySchema = z.array(
	OrganizationUserDisplaySchema,
);
export const SubOrganizationsArraySchema = z.array(SubOrganizationSchema);
export const SubOrganizationDisplaysArraySchema = z.array(
	SubOrganizationDisplaySchema,
);
export const SubContractorsArraySchema = z.array(SubContractorSchema);
export const SubContractorDisplaysArraySchema = z.array(
	SubContractorDisplaySchema,
);
export const OrganizationRoleListArraySchema = z.array(
	OrganizationRoleListItemSchema,
);
export const SubOrganizationListArraySchema = z.array(
	SubOrganizationListItemSchema,
);
export const SubContractorListArraySchema = z.array(
	SubContractorListItemSchema,
);
export const AuditLogsArraySchema = z.array(AuditLogSchema);

// Array types
export type Organizations = z.infer<typeof OrganizationsArraySchema>;
export type GlobalRoles = z.infer<typeof GlobalRolesArraySchema>;
export type OrganizationRoles = z.infer<typeof OrganizationRolesArraySchema>;
export type OrganizationRoleListItems = z.infer<
	typeof OrganizationRoleListItemsArraySchema
>;
export type OrganizationContacts = z.infer<
	typeof OrganizationContactsArraySchema
>;
export type OrganizationUsers = z.infer<typeof OrganizationUsersArraySchema>;
export type OrganizationUserDisplays = z.infer<
	typeof OrganizationUserDisplayArraySchema
>;
export type SubOrganizations = z.infer<typeof SubOrganizationsArraySchema>;
export type SubOrganizationDisplays = z.infer<
	typeof SubOrganizationDisplaysArraySchema
>;
export type SubContractors = z.infer<typeof SubContractorsArraySchema>;
export type SubContractorDisplays = z.infer<
	typeof SubContractorDisplaysArraySchema
>;
export type OrganizationRoleList = z.infer<
	typeof OrganizationRoleListArraySchema
>;
export type SubOrganizationList = z.infer<
	typeof SubOrganizationListArraySchema
>;
export type SubContractorList = z.infer<typeof SubContractorListArraySchema>;
export type AuditLogs = z.infer<typeof AuditLogsArraySchema>;

// ==================== CONSTANTS ====================

/**
 * Default pagination limits
 */
export const PAGINATION_DEFAULTS = {
	DEFAULT_LIMIT: 10,
	MAX_LIMIT: 100,
	MIN_LIMIT: 1,
} as const;

/**
 * Organization status codes
 */
export const ORGANIZATION_STATUS = {
	ACTIVE: 1,
	INACTIVE: 0,
	PENDING: 2,
	SUSPENDED: 3,
} as const;

/**
 * User status codes
 */
export const USER_STATUS = {
	ACTIVE: 1,
	INACTIVE: 0,
	PENDING: 2,
	SUSPENDED: 3,
} as const;

/**
 * Contact type codes
 */
export const CONTACT_TYPE = {
	PRIMARY: 1,
	BILLING: 2,
	TECHNICAL: 3,
	ADMINISTRATIVE: 4,
} as const;
